import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import Header from './Header';
import AdminUserGroupMembersModal from './AdminUserGroupMembersModal';
import AutoApproveGroupsModal from './AutoApproveGroupsModal';
import { getUserLevel } from '../utils/userLevel';
import { DASHBOARD_CARD_REGISTRY } from '../config/dashboardCardRegistry';
import { userMayAccessWorkspaceCard } from '../utils/workspacePerms';
import DashboardApprovalPanel from './DashboardApprovalPanel';
import DashboardLeaveMonthPanel from './DashboardLeaveMonthPanel';
import DashboardNoticeBanner from './DashboardNoticeBanner';
import DashboardTrainingIncompletePanel from './DashboardTrainingIncompletePanel';
import DashboardCardIcon from './icons/DashboardCardIcon';
import { openGoogleChat } from './icons/GoogleChatIcon';
import './Dashboard.css';

const PTR_THRESHOLD = 72;

const Dashboard = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const apiUrl = getApiBaseUrl();
  const [groupManageOpen, setGroupManageOpen] = useState(false);
  const [autoApproveOpen, setAutoApproveOpen] = useState(false);
  const email = String(user?.email || '').trim().toLowerCase();
  const isSuper = email === 'mrt@mrt.co.kr';
  const approvalStatus = String(user?.approval_status || (user?.approved === false ? 'pending' : 'approved')).trim();
  const isApproved = isSuper || approvalStatus === 'approved';
  const isPending = (!isApproved) && (approvalStatus === 'pending');
  const myLevel = useMemo(() => getUserLevel(user), [user]);
  // ✅ 사용자 관리: 슈퍼 또는 레벨 1~2 (실제 API는 CS에서 MRT 소속 등으로 추가 검증)
  const canGroupManage = isApproved && (isSuper || (myLevel != null && myLevel <= 2));

  const permissions = useMemo(() => user?.permissions || [], [user]);

  // 메뉴 권한(workspace_perms)은 DB 기준 — 관리자 변경·신규 메뉴 반영을 위해 세션 갱신
  useEffect(() => {
    const token = String(user?.token || '').trim();
    if (!token || String(user?.token_type || '').toLowerCase() !== 'session') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const newToken = String(data?.session_token || '').trim();
        if (!newToken || cancelled) return;
        let workspacePerms = user?.workspace_perms;
        try {
          const decoded = jwtDecode(newToken);
          if (Array.isArray(decoded?.workspace_perms)) {
            workspacePerms = decoded.workspace_perms;
          } else if (decoded?.workspace_perms == null) {
            workspacePerms = decoded.workspace_perms;
          }
        } catch {
          /* ignore */
        }
        const prevJson = JSON.stringify(user?.workspace_perms ?? null);
        const nextJson = JSON.stringify(workspacePerms ?? null);
        if (prevJson === nextJson && newToken === token) return;
        login({
          ...user,
          token: newToken,
          workspace_perms: workspacePerms,
          session_expires_at_ms: data?.session_expires_at_ms ?? user?.session_expires_at_ms,
        });
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 대시보드 진입 시 1회 DB 기준 권한 동기화
  }, []);

  const visibleCards = useMemo(
    () => DASHBOARD_CARD_REGISTRY.filter((card) => {
      // bypassWorkspacePerms 카드: workspace_perms 무시, eligibility만 확인
      if (card.bypassWorkspacePerms) {
        return !card.eligibility || card.eligibility({ isSuper, myLevel, permissions, isApproved });
      }
      // workspace_perms 확인 (isSuper는 항상 통과)
      if (!userMayAccessWorkspaceCard(user, card.id, isSuper)) return false;
      // workspace_perms에 명시적으로 포함된 경우 eligibility를 우선함
      // (관리자가 메뉴 설정에서 의도적으로 허용한 것으로 간주)
      return true;
    }),
    [isSuper, myLevel, user, permissions, isApproved],
  );

  const cardGridCards = useMemo(
    () => visibleCards.filter((c) => c.kind !== 'embed'),
    [visibleCards],
  );

  const showApprovalInbox = isApproved && userMayAccessWorkspaceCard(user, 'approval-inbox', isSuper);
  const showNoticeBanner = isApproved && userMayAccessWorkspaceCard(user, 'dashboard-notice', isSuper);
  const showLeavePanel = isApproved && userMayAccessWorkspaceCard(user, 'dashboard-leave', isSuper);
  const showTrainingIncomplete = isApproved && userMayAccessWorkspaceCard(user, 'dashboard-training-incomplete', isSuper);
  const showAside = showApprovalInbox || showLeavePanel || showTrainingIncomplete;

  const handleDashboardCard = (card) => {
    if (!isApproved) return;
    if (card.kind === 'embed') return;
    if (card.id === 'google-chat') {
      openGoogleChat();
      return;
    }
    if (card.kind === 'external' && card.url) {
      window.open(card.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (card.kind === 'route' && card.path) {
      guardedNav(card.path);
      return;
    }
    if (card.kind === 'action' && card.action === 'groupManage' && canGroupManage) {
      setGroupManageOpen(true);
    }
    if (card.kind === 'action' && card.action === 'autoApproveGroups' && canGroupManage) {
      setAutoApproveOpen(true);
    }
  };

  // 권한 밖인데 모달이 열린 상태면 즉시 닫기
  useEffect(() => {
    if (groupManageOpen && !canGroupManage) setGroupManageOpen(false);
  }, [groupManageOpen, canGroupManage]);

  const guardedNav = (path) => {
    if (!isApproved) return;
    navigate(path);
  };

  const token = useMemo(() => (user && typeof user === 'object' && user.token) ? String(user.token) : '', [user]);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const [signupWorking, setSignupWorking] = useState(false);
  const [signupMsg, setSignupMsg] = useState('');
  const [signupErr, setSignupErr] = useState('');
  const [approvalHasPending, setApprovalHasPending] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [pullOffset, setPullOffset] = useState(0);
  const [ptrRefreshing, setPtrRefreshing] = useState(false);
  const ptrStartYRef = useRef(0);
  const ptrPullingRef = useRef(false);
  const ptrWrapRef = useRef(null);
  const pullOffsetRef = useRef(0);

  useEffect(() => {
    pullOffsetRef.current = pullOffset;
  }, [pullOffset]);

  const handleApprovalPendingChange = useCallback((hasPending) => {
    setApprovalHasPending(!!hasPending);
  }, []);

  const triggerDashboardRefresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  const runPullRefresh = useCallback(async () => {
    if (ptrRefreshing) return;
    setPtrRefreshing(true);
    try {
      triggerDashboardRefresh();
      await new Promise((r) => setTimeout(r, 450));
    } finally {
      setPtrRefreshing(false);
      setPullOffset(0);
    }
  }, [ptrRefreshing, triggerDashboardRefresh]);

  const canStartPull = useCallback(() => {
    if (ptrRefreshing) return false;
    const scrollEl = document.scrollingElement || document.documentElement;
    return (scrollEl?.scrollTop || window.scrollY || 0) <= 0;
  }, [ptrRefreshing]);

  const onPtrTouchStart = useCallback((e) => {
    if (!canStartPull()) return;
    ptrStartYRef.current = e.touches[0].clientY;
    ptrPullingRef.current = true;
  }, [canStartPull]);

  const onPtrTouchMove = useCallback((e) => {
    if (!ptrPullingRef.current || ptrRefreshing) return;
    const dy = e.touches[0].clientY - ptrStartYRef.current;
    if (dy > 0 && canStartPull()) {
      const next = Math.min(dy * 0.55, 110);
      setPullOffset(next);
      if (next > 8) e.preventDefault();
    } else if (dy <= 0) {
      setPullOffset(0);
    }
  }, [canStartPull, ptrRefreshing]);

  const onPtrTouchEnd = useCallback(() => {
    if (!ptrPullingRef.current) return;
    ptrPullingRef.current = false;
    const offset = pullOffsetRef.current;
    if (offset >= PTR_THRESHOLD) {
      runPullRefresh();
      return;
    }
    setPullOffset(0);
  }, [runPullRefresh]);

  useEffect(() => {
    const el = ptrWrapRef.current;
    if (!el || !isApproved) return undefined;
    const opts = { passive: false };
    el.addEventListener('touchstart', onPtrTouchStart, opts);
    el.addEventListener('touchmove', onPtrTouchMove, opts);
    el.addEventListener('touchend', onPtrTouchEnd, opts);
    el.addEventListener('touchcancel', onPtrTouchEnd, opts);
    return () => {
      el.removeEventListener('touchstart', onPtrTouchStart, opts);
      el.removeEventListener('touchmove', onPtrTouchMove, opts);
      el.removeEventListener('touchend', onPtrTouchEnd, opts);
      el.removeEventListener('touchcancel', onPtrTouchEnd, opts);
    };
  }, [isApproved, onPtrTouchStart, onPtrTouchMove, onPtrTouchEnd]);

  const ptrHint = ptrRefreshing
    ? '새로고침 중…'
    : pullOffset >= PTR_THRESHOLD
      ? '놓으면 새로고침'
      : '아래로 당겨 새로고침';

  const requestSignup = async () => {
    if (signupWorking) return;
    setSignupMsg('');
    setSignupErr('');
    try {
      setSignupWorking(true);
      const kakao = String(user?.email || '').trim();
      const name = String(user?.name || '').trim();
      const res = await fetch(`${apiUrl}/api/admin_user/signup_request?_=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        // ✅ 백엔드가 body 기반으로 INSERT할 때 빈 값('')이 들어가 유니크 충돌(1062)이 나는 케이스 방지
        body: JSON.stringify({ kakao, name }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.success !== true) {
        const detail = (json && (json.detail || json.message)) ? (json.detail || json.message) : `http_${res.status}`;
        throw new Error(String(detail));
      }
      setSignupMsg('가입승인요청이 접수되었습니다.');
    } catch (e) {
      setSignupErr(String(e?.message || e));
    } finally {
      setSignupWorking(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Header />

      <main
        className={`dashboard-main${isApproved ? ' dashboard-main--approved' : ''}`}
        style={!isApproved ? {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '0 16px',
          minHeight: 'calc(100vh - 140px)',
        } : undefined}
      >
        {/* ✅ 로그인 후(승인 여부와 무관) 현재 접속 앱/도메인 식별 배너 */}
        {(!isApproved) ? (
          <div style={{ width: 'min(560px, 100%)', margin: '0 auto' }}>
            <div
              style={{
                padding: '26px 20px',
                borderRadius: '18px',
                border: '1px solid rgba(59,130,246,0.22)',
                background: 'linear-gradient(180deg, rgba(59,130,246,0.10), rgba(2,6,23,0.02))',
                boxShadow: '0 10px 28px rgba(2,6,23,0.10)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '14px',
              }}
            >
              {isPending ? (
                <div style={{ textAlign: 'center', lineHeight: 1.35 }}>
                  <div style={{ fontSize: '12px', fontWeight: 950, letterSpacing: '0.08em', color: 'rgba(37,99,235,0.92)' }}>
                    PENDING
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '26px', fontWeight: 950, color: 'var(--mrt-text)' }}>
                    가입승인 대기중입니다.
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 800, color: 'var(--mrt-text-2)' }}>
                    승인 완료 후 서비스 이용이 가능합니다.
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', lineHeight: 1.35 }}>
                  <div style={{ fontSize: '12px', fontWeight: 950, letterSpacing: '0.08em', color: 'rgba(37,99,235,0.92)' }}>
                    APPROVAL REQUIRED
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '24px', fontWeight: 950, color: 'var(--mrt-text)' }}>
                    안녕하세요{' '}
                    <span style={{ color: 'rgba(37,99,235,0.98)' }}>
                      {String(user?.name || '').trim() || '회원'}
                    </span>
                    님
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '16px', fontWeight: 800, color: 'var(--mrt-text-2)' }}>
                    서비스 이용을 위해서는 가입승인 절차가 필요합니다.
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '14px', fontWeight: 750, color: 'var(--mrt-muted)' }}>
                    아래 버튼을 눌러 요청해주세요.
                  </div>
                </div>
              )}
              {signupMsg ? (
                <div style={{ width: '100%', marginBottom: '12px', padding: '10px 12px', borderRadius: '12px', border: '1px solid rgba(34,197,94,0.25)', background: 'rgba(34,197,94,0.08)', color: 'rgba(5,150,105,0.95)', fontSize: '12px', fontWeight: 900, textAlign: 'center' }}>
                  ✅ {signupMsg}
                </div>
              ) : null}
              {signupErr ? (
                <div style={{ width: '100%', marginBottom: '12px', padding: '10px 12px', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.08)', color: 'rgba(185,28,28,0.95)', fontSize: '12px', fontWeight: 900, textAlign: 'center' }}>
                  ⚠️ {signupErr}
                </div>
              ) : null}

              {isPending ? null : (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <button
                    type="button"
                    onClick={() => requestSignup()}
                    disabled={signupWorking}
                    style={{
                      width: '320px',
                      height: '320px',
                      borderRadius: '22px',
                      border: '1px solid rgba(37,99,235,0.35)',
                      background: 'linear-gradient(135deg, rgba(37,99,235,0.98), rgba(99,102,241,0.98))',
                      cursor: signupWorking ? 'default' : 'pointer',
                      fontWeight: 980,
                      color: '#fff',
                      opacity: signupWorking ? 0.6 : 1,
                      whiteSpace: 'pre-wrap',
                      fontSize: '28px',
                      lineHeight: 1.2,
                      boxShadow: '0 18px 40px rgba(37,99,235,0.28)',
                      textShadow: '0 1px 0 rgba(0,0,0,0.12)',
                    }}
                    title="가입승인요청"
                  >
                    {signupWorking ? '요청중...' : '가입승인요청'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            ref={ptrWrapRef}
            className={`dashboard-ptr${ptrRefreshing ? ' dashboard-ptr--refreshing' : ''}`}
          >
            <div
              className="dashboard-ptr__indicator"
              style={{
                height: ptrRefreshing ? 44 : Math.max(0, pullOffset),
                opacity: pullOffset > 6 || ptrRefreshing ? 1 : 0,
              }}
              aria-live="polite"
            >
              <span className="dashboard-ptr__spinner" aria-hidden />
              <span>{ptrHint}</span>
            </div>
            <div
              className="dashboard-ptr__content"
              style={{ transform: pullOffset > 0 ? `translateY(${pullOffset}px)` : undefined }}
            >
          <div className="dashboard-approved-body">

            {showNoticeBanner ? <DashboardNoticeBanner refreshToken={refreshTick} /> : null}

            <div className={`dashboard-layout${showAside ? ' dashboard-layout--with-aside' : ''}${approvalHasPending ? ' dashboard-layout--approval-top' : ''}`}>
              <div className="dashboard-layout__main">
                <div className="dashboard-cards">
                  {cardGridCards.length === 0 ? (
                    <div className="dashboard-card dashboard-card--empty" style={{ cursor: 'default', gridColumn: '1 / -1' }}>
                      <DashboardCardIcon cardId="empty" />
                      <h3>표시할 메뉴가 없습니다</h3>
                      <p>워크스페이스 권한에 허용된 카드가 없습니다. 필요 시 관리자에게 문의하세요.</p>
                    </div>
                  ) : null}
                  {cardGridCards.map((card) => (
                    <div
                      key={card.id}
                      className="dashboard-card"
                      onClick={() => handleDashboardCard(card)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDashboardCard(card);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <DashboardCardIcon cardId={card.id} />
                      <h3>{card.title}</h3>
                    </div>
                  ))}
                </div>

                <div className="info-section dashboard-info-section">
                  <h3>시스템 정보</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">로그인 이메일:</span>
                      <span className="info-value">{user?.email}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">계정 상태:</span>
                      <span className="info-value status-active">활성</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">인증 방식:</span>
                      <span className="info-value">Google Workspace</span>
                    </div>
                  </div>
                </div>
              </div>

              {showAside ? (
                <aside className="dashboard-approval-aside" aria-label="내 결재 및 휴가·미이수 교육">
                  {showApprovalInbox ? (
                    <div className="dashboard-aside-approval">
                      <DashboardApprovalPanel
                        onHasPendingChange={handleApprovalPendingChange}
                        refreshToken={refreshTick}
                      />
                    </div>
                  ) : null}
                  {showTrainingIncomplete ? (
                    <div className="dashboard-aside-training">
                      <DashboardTrainingIncompletePanel refreshToken={refreshTick} />
                    </div>
                  ) : null}
                  {showLeavePanel ? (
                    <div className="dashboard-aside-leave">
                      <DashboardLeaveMonthPanel
                        token={token}
                        stackBelowApproval={showApprovalInbox || showTrainingIncomplete}
                        refreshToken={refreshTick}
                      />
                    </div>
                  ) : null}
                </aside>
              ) : null}
            </div>
          </div>
            </div>
          </div>
        )}
      </main>

      {canGroupManage ? (
        <AdminUserGroupMembersModal open={groupManageOpen} onClose={() => setGroupManageOpen(false)} />
      ) : null}
      {canGroupManage ? (
        <AutoApproveGroupsModal open={autoApproveOpen} onClose={() => setAutoApproveOpen(false)} />
      ) : null}
    </div>
  );
};

export default Dashboard;


